import { fieldKey, splitFieldKey } from "@/lib/pull-request-fields"
import type { AtsPullFormat, AtsPullToManyOption, AtsToManyMode } from "@/types/ats-explorer-api"

/**
 * Pure helpers for how a pull presents each selected to-many association. The modes
 * are the user's choices; the server knows List, Rows and Nested, and Excel's
 * "Separate sheet" is a JSON pull the workbook builder lays out client-side.
 */

export type PullToManyMode = "Summary" | "List" | "Rows" | "Sheet"

export const PULL_TO_MANY_MODES: readonly PullToManyMode[] = ["Summary", "List", "Rows", "Sheet"]

const DEFAULT_TO_MANY_MODE: PullToManyMode = "List"

/** Mode per to-many root, keyed like the field selection (prefixed in composite pulls) */
export type PullToManyModes = Record<string, PullToManyMode>

/** The to-many root a bare path belongs to: "commissions" for "commissions[].role" and "commissions.total" */
export function toManyRootOf(path: string): string | null {
  const match = /^([^.[\]]+)(\[\]|\.total$)/.exec(path)
  return match ? match[1] : null
}

/** Every to-many root among the given paths, once each, in order of first appearance */
export function toManyRootsOf(paths: Iterable<string>): string[] {
  return toManyRootKeysOf(paths, false)
}

/** The root key a selected key belongs to: the bare root in a single-entity pull, "{entityKey}.{root}" in a composite one */
function toManyRootKeyOf(key: string, isComposite: boolean): string | null {
  if (!isComposite) return toManyRootOf(key)
  const { entityKey, path } = splitFieldKey(key)
  const root = toManyRootOf(path)
  return root && fieldKey(entityKey, root)
}

/** The root keys of a selection, once each, in order of first appearance */
export function toManyRootKeysOf(selectedKeys: Iterable<string>, isComposite: boolean): string[] {
  const rootKeys = new Set<string>()
  for (const key of selectedKeys) {
    const rootKey = toManyRootKeyOf(key, isComposite)
    if (rootKey) rootKeys.add(rootKey)
  }
  return Array.from(rootKeys)
}

/** The sub-paths selected under a root key ("role", "user.id" for "commissions[].role", "commissions[].user.id") */
export function itemPathsUnder(selectedKeys: Iterable<string>, rootKey: string): string[] {
  const prefix = `${rootKey}[].`
  return Array.from(selectedKeys).filter((key) => key.startsWith(prefix)).map((key) => key.slice(prefix.length))
}

/** The paths Summary mode pulls: the item count and the item ids */
export function summaryPaths(root: string): string[] {
  return [`${root}.total`, `${root}[].id`]
}

export function modeOf(modes: PullToManyModes, rootKey: string): PullToManyMode {
  return modes[rootKey] ?? DEFAULT_TO_MANY_MODE
}

/** The root key laid out as rows, if any; the server accepts one per pull */
export function rowsRootKey(modes: PullToManyModes, selectedRootKeys: readonly string[]): string | null {
  return selectedRootKeys.find((key) => modes[key] === "Rows") ?? null
}

/** The root keys given their own sheet; none while a root is laid out as rows, since rows would repeat every sheet item per row */
export function sheetRootKeys(modes: PullToManyModes, selectedRootKeys: readonly string[]): string[] {
  if (rowsRootKey(modes, selectedRootKeys)) return []
  return selectedRootKeys.filter((key) => modes[key] === "Sheet")
}

/**
 * The modes of the roots still selected, so a stale choice never reaches the server or
 * storage. Summary narrows a root to its count and ids, so selecting more under it ends
 * the Summary and the root falls back to the default.
 */
export function activeToManyModes(modes: PullToManyModes, selectedKeys: Iterable<string>, isComposite: boolean): PullToManyModes {
  const keys = Array.from(selectedKeys)
  const outgrown = (rootKey: string) => keys.some((key) => toManyRootKeyOf(key, isComposite) === rootKey && !summaryPaths(rootKey).includes(key))
  return Object.fromEntries(
    toManyRootKeysOf(keys, isComposite)
      .filter((rootKey) => rootKey in modes && !(modes[rootKey] === "Summary" && outgrown(rootKey)))
      .map((rootKey) => [rootKey, modes[rootKey]])
  )
}

/** The modes keyed under a renamed related entity, re-keyed under its new name */
export function renameSectionModes(modes: PullToManyModes, from: string, to: string): PullToManyModes {
  return rekeyModes(modes, (key) => (splitFieldKey(key).entityKey === from ? fieldKey(to, splitFieldKey(key).path) : key))
}

/** The modes without one section's, for a related entity whose type changed */
export function withoutSectionModes(modes: PullToManyModes, entityKey: string): PullToManyModes {
  return rekeyModes(modes, (key) => (splitFieldKey(key).entityKey === entityKey ? null : key))
}

function rekeyModes(modes: PullToManyModes, rekey: (key: string) => string | null): PullToManyModes {
  return Object.fromEntries(
    Object.entries(modes).flatMap(([key, mode]) => {
      const next = rekey(key)
      return next === null ? [] : [[next, mode] as const]
    })
  )
}

export interface PullToManyPlan {
  /** The format the server is asked for */
  format: "Csv" | "Json"
  toMany: AtsPullToManyOption[] | null
  /** Root keys written to their own sheet of the workbook */
  sheetKeys: string[]
}

/** The parent id column a sheet is keyed by: "id", or "{entityKey}.id" in a composite pull */
export function parentIdPathOf(rootKey: string, isComposite: boolean): string {
  return isComposite ? fieldKey(splitFieldKey(rootKey).entityKey, "id") : "id"
}

/**
 * The server-side layout for the chosen file format. JSON keeps every association
 * nested and complete. CSV sends the chosen mode, a sheet falling back to the in-cell
 * list. Excel with a sheet-mode field pulls JSON (nested for the sheet fields, the
 * chosen mode for the rest) and leaves the layout to the workbook builder. One root
 * at most is laid out as rows, and a sheet never coexists with it.
 */
export function planToMany(modes: PullToManyModes, selectedRootKeys: readonly string[], format: AtsPullFormat): PullToManyPlan {
  if (format === "Json" || selectedRootKeys.length === 0) {
    return { format: format === "Json" ? "Json" : "Csv", toMany: null, sheetKeys: [] }
  }
  const rowsKey = rowsRootKey(modes, selectedRootKeys)
  const sheetKeys = format === "Xlsx" ? sheetRootKeys(modes, selectedRootKeys) : []
  const serverMode = (key: string): AtsToManyMode => {
    if (key === rowsKey) return "Rows"
    return sheetKeys.includes(key) ? "Nested" : "List"
  }
  return {
    format: sheetKeys.length > 0 ? "Json" : "Csv",
    toMany: selectedRootKeys.map((field) => ({ field, mode: serverMode(field) })),
    sheetKeys,
  }
}
